import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { angebotsvorlagenAPI } from '@/lib/api';

/* „Angebote (Check-up)“ — Einrichtung der Angebotsvorlagen (Coach-Cockpit W1, backend/angebotsvorlagen.py). Startbestand und
   Preise kommen per Seed (scripts/seed_checkup_angebote.py); hier pflegt die Leitung sie. Vergleichspreis und Ersparnis rechnet
   der Server. Der Preis des Ernährungscoachings steht unter Ernährung › Einstellungen (keine zweite Preisquelle). Eine Änderung
   gilt nur für neue Angebote — verkaufte Pakete und gesendete Angebote behalten ihren Preis. */

const ABRECHNUNG = { einmalig: 'einmalig', monatlich: 'monatlich', monatlich_n: 'monatlich, feste Monate' };
const euro = (v) => (v == null ? '–' : `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`);
const zahlOderNull = (v) => (v === '' || v == null ? null : Number(v));

function Zeile({ v, darf, onGespeichert }) {
  const [d, setD] = useState(v);
  const [offen, setOffen] = useState(false);
  const [arbeitet, setArbeitet] = useState(false);
  useEffect(() => setD(v), [v]);
  const ern = v.schluessel === 'ernaehrung';
  const feld = (k, wert) => setD((x) => ({ ...x, [k]: wert }));
  const speichern = async () => {
    setArbeitet(true);
    try {
      const daten = {
        name: d.name, inhalt: d.inhalt, label: d.label || null, sortierung: Number(d.sortierung) || 0, aktiv: !!d.aktiv,
        ...(ern ? {} : {
          preis: zahlOderNull(d.preis), termine: Number(d.termine) || 0, einloesefrist_tage: zahlOderNull(d.einloesefrist_tage),
          verfaellt: !!d.verfaellt, testtermin_nach_tagen: zahlOderNull(d.testtermin_nach_tagen),
          bonus: { ernaehrung_gratis_tage: zahlOderNull(d.bonus?.ernaehrung_gratis_tage) },
        }),
      };
      await angebotsvorlagenAPI.setzen(v.schluessel, daten);
      toast.success('Gespeichert – gilt für neue Angebote');
      setOffen(false);
      onGespeichert();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setArbeitet(false); }
  };
  const eingabe = 'h-11 rounded-md border border-border bg-background px-2 text-sm w-full';
  return (
    <li className="py-3 space-y-2" data-testid={`vorlage-${v.schluessel}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-bold text-sm">{v.name}</span>
        {v.label && <span className="text-[11px] rounded-full border border-marke/40 text-marke px-2 py-0.5">{v.label}</span>}
        {!v.aktiv && <span className="text-[11px] text-muted-foreground">(aus)</span>}
        <span className="ml-auto text-sm tabular-nums">
          {ern ? 'Preis aus Ernährung › Einstellungen' : `${euro(v.preis)} ${ABRECHNUNG[v.abrechnung] || v.abrechnung}${v.monate ? ` × ${v.monate}` : ''}`}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        {v.inhalt}{v.termine ? ` · ${v.termine} PT-Termin${v.termine === 1 ? "" : "e"}${v.abrechnung === 'monatlich_n' ? ' je Monat' : ''}` : ''}
        {v.einloesefrist_tage ? ` · Einlösefrist ${v.einloesefrist_tage} Tage${v.verfaellt === false ? ', verfällt nicht' : ''}` : ''}
        {v.testtermin_nach_tagen ? ` · Testtermin nach ${v.testtermin_nach_tagen} Tagen` : ''}
        {v.bonus?.ernaehrung_gratis_tage ? ` · Ernährung ${v.bonus.ernaehrung_gratis_tage} Tage gratis` : ''}
        {v.nur_im_ergebnis_checkup ? ' · nur im Ergebnis-Check-up' : ''}
        {v.ersparnis ? ` · Vergleich ${euro(v.vergleichspreis)}/Monat, Ersparnis ${euro(v.ersparnis)}` : ''}
      </p>
      {darf && !offen && <Button size="sm" variant="outline" className="h-11" onClick={() => setOffen(true)}>Bearbeiten</Button>}
      {darf && offen && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <label className="col-span-2">Name<input className={eingabe} value={d.name || ''} onChange={(e) => feld('name', e.target.value)} /></label>
          <label className="col-span-2">Inhalt<input className={eingabe} value={d.inhalt || ''} onChange={(e) => feld('inhalt', e.target.value)} /></label>
          {!ern && <label>Preis (€)<input type="number" step="0.01" className={eingabe} value={d.preis ?? ''} onChange={(e) => feld('preis', e.target.value)} /></label>}
          {!ern && <label>PT-Termine<input type="number" className={eingabe} value={d.termine ?? ''} onChange={(e) => feld('termine', e.target.value)} /></label>}
          {!ern && <label>Einlösefrist (Tage)<input type="number" className={eingabe} value={d.einloesefrist_tage ?? ''} onChange={(e) => feld('einloesefrist_tage', e.target.value)} /></label>}
          {!ern && <label>Testtermin nach (Tage)<input type="number" className={eingabe} value={d.testtermin_nach_tagen ?? ''} onChange={(e) => feld('testtermin_nach_tagen', e.target.value)} /></label>}
          {!ern && <label>Ernährung gratis (Tage)<input type="number" className={eingabe} value={d.bonus?.ernaehrung_gratis_tage ?? ''} onChange={(e) => feld('bonus', { ernaehrung_gratis_tage: e.target.value })} /></label>}
          <label>Label<input className={eingabe} value={d.label || ''} onChange={(e) => feld('label', e.target.value)} /></label>
          <label>Sortierung<input type="number" className={eingabe} value={d.sortierung ?? 0} onChange={(e) => feld('sortierung', e.target.value)} /></label>
          {!ern && <label className="flex items-center gap-2 min-h-[44px]"><input type="checkbox" className="h-5 w-5" checked={d.verfaellt !== false} onChange={(e) => feld('verfaellt', e.target.checked)} /> verfällt nach der Frist</label>}
          <label className="flex items-center gap-2 min-h-[44px]"><input type="checkbox" className="h-5 w-5" checked={!!d.aktiv} onChange={(e) => feld('aktiv', e.target.checked)} /> aktiv</label>
          <div className="col-span-2 md:col-span-4 flex gap-2">
            <Button size="sm" className="h-11" disabled={arbeitet} onClick={speichern}>{arbeitet ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Speichern'}</Button>
            <Button size="sm" variant="ghost" className="h-11" onClick={() => { setD(v); setOffen(false); }}>Abbrechen</Button>
          </div>
        </div>
      )}
    </li>
  );
}

export default function AngebotsvorlagenKarte({ laden }) {
  // `laden`: anderer Abruf (z. B. /coach über die Trainer-Sitzung, nur lesen); ohne ihn der CRM-Weg
  const [daten, setDaten] = useState(null);
  const lade = useCallback(async () => {
    try { setDaten((await (laden ? laden() : angebotsvorlagenAPI.liste())).data); } catch { setDaten({ vorlagen: [], darf_bearbeiten: false, fehler: true }); }
  }, [laden]);
  useEffect(() => { lade(); }, [lade]);
  return (
    <section className="rounded-[14px] border border-border bg-card px-5 py-[18px]" data-testid="angebotsvorlagen-karte">
      <div className="space-y-2">
        <h2 className="text-base font-bold">Einzelleistungen</h2>
        <p className="text-sm text-muted-foreground">Was der Check-up anbietet, jede Leistung mit eigenem Preis. Änderungen gelten nur für neue Angebote. Bearbeiten darf nur die Leitung.</p>
        {daten === null ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : daten.fehler
          ? <p className="text-sm text-muted-foreground">Die Angebote lassen sich auf dieser Seite gerade nicht laden — sie sind im CRM unter Einstellungen erreichbar.</p>
          : daten.vorlagen.length === 0
          ? <p className="text-sm text-muted-foreground">Noch nicht eingerichtet (scripts/seed_checkup_angebote.py).</p>
          : <ul className="divide-y divide-border">{daten.vorlagen.map((v) => <Zeile key={v.schluessel} v={v} darf={!!daten.darf_bearbeiten} onGespeichert={lade} />)}</ul>}
      </div>
    </section>
  );
}
